import React, { useState, useMemo, useEffect } from 'react';
import { useFacilityAvailability } from '../../hooks/useFacilityAvailability';
import { FACILITIES, getFacilityById } from '../../hooks/facilityConfig';
import { AVAILABILITY_STATUS, formatDateDisplay, getAvailableTimeRanges, formatTimeRange } from '../../hooks/availabilityUtils';
import './MonthlyCalendarView.css';

const MonthlyCalendarView = () => {
  const [selectedFacilityId, setSelectedFacilityId] = useState('bar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayDetails, setShowDayDetails] = useState(false);
  const [viewMode, setViewMode] = useState('future'); // 'future' or 'historical'
  const [historicalDayData, setHistoricalDayData] = useState(null);
  const [historicalCache, setHistoricalCache] = useState({});
  const [loadingHistorical, setLoadingHistorical] = useState(false);
  const [historicalError, setHistoricalError] = useState(null);

  // Fetch availability data
  const { data, loading, error, refetch } = useFacilityAvailability({
    facilityIds: [selectedFacilityId],
    days: 30,
    autoRefetch: true,
    refetchInterval: 5 * 60 * 1000 // 5 minutes
  });

  // Get facility data
  const facilityData = data?.facilities?.[selectedFacilityId];

  const formatISODate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getMonthKey = (date, facilityId) => (
    `${facilityId}-${date.getFullYear()}-${date.getMonth()}`
  );

  const monthKey = getMonthKey(currentDate, selectedFacilityId);
  const historicalMonthData = historicalCache[monthKey];
  const historicalMonthStatus = historicalMonthData?.status;

  useEffect(() => {
    if (viewMode !== 'historical') {
      setLoadingHistorical(false);
      setHistoricalError(null);
      return;
    }

    if (historicalMonthStatus === 'loaded' || historicalMonthStatus === 'loading') {
      return;
    }

    let isMounted = true;

    const fetchHistoricalData = async () => {
      const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      const startDate = formatISODate(firstDay);
      const endDate = formatISODate(lastDay);
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://mansion-facility-wtr-reservation-api.info1499.workers.dev';

      setLoadingHistorical(true);
      setHistoricalError(null);
      setHistoricalCache(prev => ({
        ...prev,
        [monthKey]: { status: 'loading' }
      }));

      try {
        const response = await fetch(
          `${baseUrl}/api/v1/analytics/historical/${selectedFacilityId}?startDate=${startDate}&endDate=${endDate}`
        );

        if (!response.ok) {
          throw new Error(`過去データの取得に失敗しました (${response.status})`);
        }

        const json = await response.json();
        const records = Array.isArray(json.data) ? json.data : [];
        const dataMap = records.reduce((acc, record) => {
          if (record?.date) {
            const key = record.date.replace(/\//g, '-');
            acc[key] = record;
          }
          return acc;
        }, {});

        if (isMounted) {
          setHistoricalCache(prev => ({
            ...prev,
            [monthKey]: {
              status: 'loaded',
              dataMap,
              records,
              statistics: json.statistics || null
            }
          }));
          setLoadingHistorical(false);
        }
      } catch (error) {
        console.error('Failed to fetch historical data:', error);
        if (isMounted) {
          setHistoricalError(error.message);
          setHistoricalCache(prev => ({
            ...prev,
            [monthKey]: {
              status: 'error',
              error: error.message
            }
          }));
          setLoadingHistorical(false);
        }
      }
    };

    fetchHistoricalData();

    return () => {
      isMounted = false;
    };
  }, [viewMode, monthKey, selectedFacilityId, currentDate, historicalMonthStatus]);

  useEffect(() => {
    if (viewMode !== 'historical' || !selectedDay || !selectedDay.isPast) {
      return;
    }

    const keyForDay = getMonthKey(selectedDay.date, selectedFacilityId);
    const cacheEntry = historicalCache[keyForDay];
    if (cacheEntry?.status === 'loaded') {
      const entry = cacheEntry.dataMap?.[selectedDay.dateString];
      setHistoricalDayData(entry || null);
    }
  }, [viewMode, selectedDay, selectedFacilityId, historicalCache]);

  // Calendar calculations
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    // Create availability map for quick lookup
    const availabilityMap = new Map();
    if (facilityData?.days) {
      facilityData.days.forEach(day => {
        const dateKey = day.date.replace(/\//g, '-');
        availabilityMap.set(dateKey, day);
      });
    }

    // Generate calendar days
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({ empty: true });
    }
    
    // Add days of the month
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = availabilityMap.get(dateString);
      const historicalEntry = historicalMonthData?.dataMap?.[dateString];
      const isPast = date < today;
      const isToday = date.toDateString() === today.toDateString();
      
      let status;
      if (viewMode === 'historical' && isPast) {
        if (historicalEntry) {
          const occupancy = historicalEntry.occupancyRate || 0;
          if (occupancy <= 0) {
            status = AVAILABILITY_STATUS.AVAILABLE;
          } else if (occupancy >= 0.999) {
            status = AVAILABILITY_STATUS.UNAVAILABLE;
          } else {
            status = AVAILABILITY_STATUS.PARTIALLY_AVAILABLE;
          }
        } else {
          status = AVAILABILITY_STATUS.UNKNOWN;
        }
      } else {
        status = isPast ? 'past' : (dayData?.status || AVAILABILITY_STATUS.UNKNOWN);
      }

      days.push({
        day,
        date,
        dateString,
        isPast,
        isToday,
        availability: dayData,
        status,
        historical: historicalEntry
      });
    }
    
    return days;
  }, [currentDate, facilityData, viewMode, historicalMonthData]);

  // Navigation handlers
  const goToPreviousMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
    setSelectedDay(null);
    setShowDayDetails(false);
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
    setSelectedDay(null);
    setShowDayDetails(false);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDay(null);
    setShowDayDetails(false);
  };

  // Day click handler
  const handleDayClick = (day) => {
    if (day.empty) return;
    
    // Allow clicking on past dates in historical mode
    if (viewMode === 'historical') {
      setSelectedDay(day);
      setShowDayDetails(true);
      setHistoricalDayData(null);
    } else if (!day.isPast) {
      // Future mode - only allow clicking on future dates
      setSelectedDay(day);
      setShowDayDetails(true);
    }
  };

  // Facility change handler
  const handleFacilityChange = (e) => {
    setSelectedFacilityId(e.target.value);
    setSelectedDay(null);
    setShowDayDetails(false);
    setHistoricalDayData(null);
  };
  
  // View mode change handler
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setSelectedDay(null);
    setShowDayDetails(false);
    setHistoricalDayData(null);
  };

  // Format month/year for display
  const monthYearDisplay = currentDate.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'long'
  });

  // Format time from minutes
  const formatTimeFromMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Calculate booked time bands for visualization
  const getBookedBands = (dayData) => {
    if (!dayData?.slots || dayData.slots.length === 0) {
      return [];
    }

    const facility = getFacilityById(selectedFacilityId);
    if (!facility.openTime || !facility.closeTime) return [];

    // Parse facility hours
    const [openHour, openMin] = facility.openTime.split(':').map(Number);
    const [closeHour, closeMin] = facility.closeTime.split(':').map(Number);
    const openMinutes = openHour * 60 + openMin;
    const closeMinutes = closeHour * 60 + closeMin;
    const totalMinutes = closeMinutes - openMinutes;

    // Group consecutive booked slots into bands
    const bands = [];
    let currentBand = null;

    dayData.slots.forEach((slot, index) => {
      // Check both conditions - either not available OR status is booked
      if ((!slot.available || slot.status === 'booked') && slot.status !== 'past-deadline') {
        const [hour, min] = slot.time.split(':').map(Number);
        const slotMinutes = hour * 60 + min;
        
        if (!currentBand) {
          currentBand = {
            startMinutes: slotMinutes,
            endMinutes: slotMinutes + 30 // Each slot is 30 minutes
          };
        } else if (slotMinutes === currentBand.endMinutes) {
          // Extend current band
          currentBand.endMinutes = slotMinutes + 30;
        } else {
          // Save current band and start new one
          bands.push({...currentBand});
          currentBand = {
            startMinutes: slotMinutes,
            endMinutes: slotMinutes + 30
          };
        }
      } else if (currentBand) {
        // Save current band when we hit an available slot
        bands.push({...currentBand});
        currentBand = null;
      }
    });

    // Don't forget the last band
    if (currentBand) {
      bands.push(currentBand);
    }

    // Convert to percentages for CSS positioning and add time range
    return bands.map(band => ({
      top: `${((band.startMinutes - openMinutes) / totalMinutes) * 100}%`,
      height: `${((band.endMinutes - band.startMinutes) / totalMinutes) * 100}%`,
      timeRange: `${formatTimeFromMinutes(band.startMinutes)}-${formatTimeFromMinutes(band.endMinutes)}`
    }));
  };

  // Get booked time ranges for tooltip
  const getBookedTimeRanges = (availability) => {
    if (!availability) return '予約情報なし';
    const bands = getBookedBands(availability);
    if (bands.length === 0) return '予約情報なし';
    return bands.map(band => band.timeRange).join(', ') + ' 予約済';
  };

  // Get day cell class
  const getDayCellClass = (day) => {
    if (day.empty) return 'calendar-day empty';
    
    let classes = ['calendar-day'];
    
    if (day.isPast) {
      // In historical mode, past dates should be clickable
      if (viewMode === 'historical') {
        classes.push('past-clickable');
      } else {
        classes.push('past');
      }
    } else if (day.isToday) {
      classes.push('today');
    }
    
    // Add status-based classes
    switch (day.status) {
      case AVAILABILITY_STATUS.AVAILABLE:
        classes.push('available');
        break;
      case AVAILABILITY_STATUS.PARTIALLY_AVAILABLE:
        classes.push('partial');
        break;
      case AVAILABILITY_STATUS.UNAVAILABLE:
        classes.push('unavailable');
        break;
      default:
        if (!day.isPast) {
          classes.push('unknown');
        }
    }
    
    if (selectedDay && selectedDay.dateString === day.dateString) {
      classes.push('selected');
    }

    // Add class if day has booking patterns
    const bands = getBookedBands(day);
    if (bands.length > 0) {
      classes.push('has-patterns');
    }
    
    return classes.join(' ');
  };

  // Get status emoji
  const getStatusEmoji = (status) => {
    switch (status) {
      case AVAILABILITY_STATUS.AVAILABLE:
        return '✅';
      case AVAILABILITY_STATUS.PARTIALLY_AVAILABLE:
        return '⚠️';
      case AVAILABILITY_STATUS.UNAVAILABLE:
        return '❌';
      default:
        return '❓';
    }
  };

  // Format available times for day details
  const formatAvailableTimes = (day) => {
    if (!day.availability?.slots) return 'データなし';
    
    const availableRanges = getAvailableTimeRanges(day.availability.slots);
    
    if (availableRanges.length === 0) {
      return '利用不可';
    }
    
    return availableRanges.map(range => formatTimeRange(range)).join(', ');
  };

  return (
    <div className="monthly-calendar-view">
      {/* View Mode Toggle */}
      <div className="view-mode-toggle">
        <button 
          className={`mode-button ${viewMode === 'future' ? 'active' : ''}`}
          onClick={() => handleViewModeChange('future')}
        >
          予約可能日
        </button>
        <button 
          className={`mode-button ${viewMode === 'historical' ? 'active' : ''}`}
          onClick={() => handleViewModeChange('historical')}
        >
          過去の利用状況
        </button>
      </div>
      
      {/* Header */}
      <div className="calendar-header">
        <div className="facility-selector">
          <label htmlFor="facility-select">施設を選択:</label>
          <select
            id="facility-select"
            value={selectedFacilityId}
            onChange={handleFacilityChange}
            className="facility-dropdown"
          >
            {Object.values(FACILITIES).map(f => (
              <option key={f.id} value={f.id}>
                {f.icon} {f.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="month-navigation">
          <button
            className="nav-button"
            onClick={goToPreviousMonth}
            aria-label="前月"
          >
            ‹
          </button>
          
          <h2 className="month-year">{monthYearDisplay}</h2>
          
          <button
            className="nav-button"
            onClick={goToNextMonth}
            aria-label="翌月"
          >
            ›
          </button>
          
          <button
            className="today-button"
            onClick={goToToday}
            aria-label="今日"
          >
            今日
          </button>
        </div>
      </div>

      {/* Loading/Error states */}
      {loading && (
        <div className="loading-state">
          <div className="loading-spinner">読み込み中...</div>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>エラーが発生しました: {error}</p>
          <button onClick={refetch} className="retry-button">
            再試行
          </button>
        </div>
      )}

      {viewMode === 'historical' && historicalError && (
        <div className="error-state">
          <p>過去データの読み込みに失敗しました: {historicalError}</p>
        </div>
      )}

      {/* Calendar Grid */}
      {!loading && !error && (
        <>
          <div className="calendar-grid">
            {/* Day headers */}
            <div className="weekday-headers">
              {['日', '月', '火', '水', '木', '金', '土'].map(day => (
                <div key={day} className="weekday-header">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar days */}
            <div className="calendar-days">
              {calendarData.map((day, index) => (
                <div
                  key={index}
                  className={getDayCellClass(day)}
                  onClick={() => handleDayClick(day)}
                  role="button"
                  tabIndex={day.empty || (viewMode === 'future' && day.isPast) ? -1 : 0}
                  aria-label={day.empty ? '' : `${day.day}日`}
                  title={(() => {
                    if (day.empty) return '';
                    if (viewMode === 'historical' && day.isPast) {
                      if (historicalMonthStatus === 'loading') return '過去データを取得中';
                      return day.historical
                        ? `利用率 ${Math.round(day.historical.occupancyRate * 100)}%`
                        : '履歴データなし';
                    }
                    if (!day.isPast && day.status === AVAILABILITY_STATUS.PARTIALLY_AVAILABLE) {
                      return getBookedTimeRanges(day.availability);
                    }
                    return '';
                  })()}
                >
                  {!day.empty && (
                    <>
                      <div className="day-number">{day.day}</div>
                      {viewMode === 'historical' && day.isPast && (
                        <>
                          <div className="historical-indicator">📊</div>
                          <div className="historical-status" title={day.historical ? `利用率 ${Math.round(day.historical.occupancyRate * 100)}%` : (historicalMonthStatus === 'loading' ? '読み込み中' : 'データなし')}>
                            {historicalMonthStatus === 'loading'
                              ? '…'
                              : day.historical
                                ? `${Math.round(day.historical.occupancyRate * 100)}%`
                                : '—'}
                          </div>
                        </>
                      )}
                      {(!day.isPast || viewMode !== 'historical') && !day.isPast && day.status !== 'past' && (
                        <>
                          {day.status === AVAILABILITY_STATUS.PARTIALLY_AVAILABLE && day.availability?.slots && (
                            <div className="day-schedule-bar">
                              {(() => {
                                const facility = getFacilityById(selectedFacilityId);
                                if (!facility) return null;
                                const openHour = parseInt(facility.openTime?.split(':')[0] || '10');
                                const closeHour = parseInt(facility.closeTime?.split(':')[0] || '22');
                                const totalHours = closeHour - openHour;
                                
                                // Calculate booked slots
                                const bookedSlots = day.availability.slots.filter(slot => !slot.available);
                                const bookedRanges = [];
                                
                                // Group consecutive booked slots
                                let currentRange = null;
                                bookedSlots.forEach((slot, index) => {
                                  const [hour, minute] = slot.time.split(':').map(Number);
                                  const slotHour = hour + (minute / 60);
                                  
                                  if (!currentRange) {
                                    currentRange = { start: slotHour, end: slotHour + 0.5 };
                                  } else if (slotHour === currentRange.end) {
                                    currentRange.end = slotHour + 0.5;
                                  } else {
                                    bookedRanges.push(currentRange);
                                    currentRange = { start: slotHour, end: slotHour + 0.5 };
                                  }
                                });
                                if (currentRange) bookedRanges.push(currentRange);
                                
                                // Render booked bars
                                return bookedRanges.map((range, idx) => {
                                  const startPercent = ((range.start - openHour) / totalHours) * 100;
                                  const widthPercent = ((range.end - range.start) / totalHours) * 100;
                                  
                                  return (
                                    <div
                                      key={idx}
                                      className="booked-bar"
                                      style={{
                                        left: `${startPercent}%`,
                                        width: `${widthPercent}%`
                                      }}
                                    />
                                  );
                                });
                              })()}
                            </div>
                          )}
                          <div className="day-status">
                            {getStatusEmoji(day.status)}
                          </div>
                        </>
                      )}
                      {/* Hover tooltip - render first so it's not covered */}
                      {!day.isPast && day.availability?.slots && day.status === AVAILABILITY_STATUS.PARTIALLY_AVAILABLE && (
                        <div className="booking-tooltip" title={getBookedTimeRanges(day.availability)}>
                          {getBookedTimeRanges(day.availability)}
                        </div>
                      )}
                      {/* Booking visualization patterns */}
                      {!day.isPast && day.availability?.slots && day.status === AVAILABILITY_STATUS.PARTIALLY_AVAILABLE && (
                        <div className="booking-patterns">
                          {getBookedBands(day.availability).map((band, index) => (
                            <div
                              key={index}
                              className="booking-band"
                              style={{
                                top: band.top,
                                height: band.height
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="calendar-legend">
            <div className="legend-item">
              <span className="legend-color available"></span>
              <span>利用可能</span>
            </div>
            <div className="legend-item">
              <span className="legend-color partial"></span>
              <span>一部利用可能</span>
            </div>
            <div className="legend-item">
              <span className="legend-color unavailable"></span>
              <span>利用不可</span>
            </div>
            <div className="legend-item">
              <span className="legend-color unknown"></span>
              <span>データなし</span>
            </div>
          </div>

          {/* Day Details Modal */}
          {showDayDetails && selectedDay && (() => {
            const facility = getFacilityById(selectedFacilityId);
            if (!facility) return null;
            
            return (
              <div className="day-details-modal" onClick={() => setShowDayDetails(false)}>
                <div className="day-details-content" onClick={e => e.stopPropagation()}>
                  <button
                    className="close-button"
                    onClick={() => setShowDayDetails(false)}
                    aria-label="閉じる"
                  >
                    ×
                  </button>
                  
                  <h3>{facility.icon} {facility.name}</h3>
                <p className="detail-date">
                  {formatDateDisplay(selectedDay.dateString.replace(/-/g, '/'))}
                </p>
                
                {viewMode === 'historical' && selectedDay.isPast ? (
                  // Historical mode - show past usage data
                  <>
                    {historicalError ? (
                      <div className="no-historical-data">
                        <p>過去データの取得に失敗しました: {historicalError}</p>
                      </div>
                    ) : loadingHistorical && (!historicalMonthData || historicalMonthData.status === 'loading') ? (
                      <div className="loading-spinner">読み込み中...</div>
                    ) : historicalDayData ? (
                      <>
                        <div className="detail-status">
                          <strong>利用率:</strong>
                          <span className="status-badge">
                            {(historicalDayData.occupancyRate * 100).toFixed(1)}%
                          </span>
                        </div>
                        
                        <div className="detail-stats">
                          <strong>利用統計:</strong>
                          <p>予約済みスロット: {historicalDayData.bookedSlots}/{historicalDayData.totalSlots}</p>
                          <p>利用可能スロット: {historicalDayData.availableSlots}</p>
                        </div>
                        
                        {historicalDayData.peakHours && historicalDayData.peakHours.length > 0 && (
                          <div className="detail-peak">
                            <strong>ピーク時間:</strong>
                            <p>{historicalDayData.peakHours.join(', ')}</p>
                          </div>
                        )}
                        
                        {historicalDayData.patterns && (
                          <div className="detail-patterns">
                            <strong>時間帯別利用率:</strong>
                            <p>朝 (5-12時): {(historicalDayData.patterns.morning * 100).toFixed(0)}%</p>
                            <p>昼 (12-17時): {(historicalDayData.patterns.afternoon * 100).toFixed(0)}%</p>
                            <p>夜 (17-23時): {(historicalDayData.patterns.evening * 100).toFixed(0)}%</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="no-historical-data">
                        <p>この日の履歴データはありません</p>
                      </div>
                    )}
                  </>
                ) : (
                  // Future mode - show availability
                  <>
                    <div className="detail-status">
                      <strong>ステータス:</strong>
                      <span className={`status-badge ${selectedDay.status}`}>
                        {getStatusEmoji(selectedDay.status)} {
                          selectedDay.status === AVAILABILITY_STATUS.AVAILABLE ? '利用可能' :
                          selectedDay.status === AVAILABILITY_STATUS.PARTIALLY_AVAILABLE ? '一部利用可能' :
                          selectedDay.status === AVAILABILITY_STATUS.UNAVAILABLE ? '利用不可' :
                          'データなし'
                        }
                      </span>
                    </div>
                    
                    {selectedDay.availability && (
                      <div className="detail-times">
                        <strong>利用可能時間:</strong>
                        <p>{formatAvailableTimes(selectedDay)}</p>
                      </div>
                    )}
                  </>
                )}
                
                {facility.type === 'daily' && (
                  <div className="detail-note">
                    <small>
                      チェックイン: {facility.checkInTime}<br />
                      チェックアウト: {facility.checkOutTime}
                    </small>
                  </div>
                )}
              </div>
            </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default MonthlyCalendarView;
